/**
 * Utility functions for handling file downloads from API responses
 */

/**
 * Downloads a blob as a file
 * @param blob - The blob to download
 * @param filename - The filename for the download
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
	// Create a URL for the blob
	const url = window.URL.createObjectURL(blob);
	
	// Create a temporary link element
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	
	// Append to body, click, and remove
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	
	// Clean up the URL
	window.URL.revokeObjectURL(url);
};

/**
 * Handles API response that might be a file download
 * @param response - The API response
 * @param defaultFilename - Default filename if not provided in headers
 */
export const handleFileResponse = (response: any, defaultFilename: string = 'download'): void => {
	// Check for the new file response structure
	if (response && response.__isFileResponse && response.fileId) {
		// Retrieve blob from global storage
		const fileData = (window as any).__fileDownloads?.[response.fileId];
		if (fileData && fileData.blob instanceof Blob) {
			let filename = defaultFilename;
			
			// Extract filename from Content-Disposition header if available
			if (response.contentDisposition) {
				const filenameMatch = response.contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
			
			// Add appropriate extension based on content type
			if (response.contentType) {
				if (response.contentType.includes('excel') || response.contentType.includes('spreadsheet')) {
					if (!filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
						filename += '.xlsx';
					}
				} else if (response.contentType.includes('pdf')) {
					if (!filename.endsWith('.pdf')) {
						filename += '.pdf';
					}
				}
			}
			
			downloadBlob(fileData.blob, filename);
			
			// Clean up the global storage
			delete (window as any).__fileDownloads[response.fileId];
		} else {
			console.error('File data not found in global storage:', response.fileId);
		}
	} else if (response && response.__isFileResponse && response.blob instanceof Blob) {
		// Fallback for old structure
		let filename = defaultFilename;
		
		if (response.contentDisposition) {
			const filenameMatch = response.contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
			if (filenameMatch && filenameMatch[1]) {
				filename = filenameMatch[1].replace(/['"]/g, '');
			}
		}
		
		if (response.contentType) {
			if (response.contentType.includes('excel') || response.contentType.includes('spreadsheet')) {
				if (!filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
					filename += '.xlsx';
				}
			} else if (response.contentType.includes('pdf')) {
				if (!filename.endsWith('.pdf')) {
					filename += '.pdf';
				}
			}
		}
		
		downloadBlob(response.blob, filename);
	} else if (response instanceof Blob) {
		// Handle direct blob response (fallback)
		downloadBlob(response, defaultFilename);
	} else if (response && response.data instanceof Blob) {
		// Handle response.data blob (fallback)
		downloadBlob(response.data, defaultFilename);
	} else {
		console.error('Response is not a file response:', response);
	}
};

/**
 * Creates a download URL for a blob (useful for React Native)
 * @param blob - The blob to create URL for
 * @returns The blob URL
 */
export const createBlobUrl = (blob: Blob): string => {
	return window.URL.createObjectURL(blob);
};
